import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@config/server/apiClient';
import { inventoryEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

export interface SyncStatusCache {
	id: string;
	syncedAt: number;
	success: boolean;
}

export interface SyncInventoryResponse {
	success: boolean;
	message?: string;
	data?: {
		scrapeTriggered: boolean;
		dmsSyncTriggered: boolean;
		scrapeMessageId?: string;
		dmsSyncResults?: Array<{ inventoryId: string; success: boolean; error?: string }>;
	};
}

export interface SyncInventoryParams {
	servicesProviderId: string;
	inventoryIds: string[];
}

export const SYNC_COOLDOWN_MS = 60 * 60 * 1000;

export const syncInventoryItem = async (
	params: SyncInventoryParams,
): Promise<SyncInventoryResponse> => {
	const response = await apiClient.post<SyncInventoryResponse>(
		inventoryEndpoints.sync(params.servicesProviderId),
		{
			inventoryIds: params.inventoryIds,
		},
	);
	return response.data;
};

export const getSyncCooldownRemaining = (syncStatus: SyncStatusCache | undefined): number => {
	if (!syncStatus?.success) {
		return 0;
	}

	const elapsed = Date.now() - syncStatus.syncedAt;
	const remaining = SYNC_COOLDOWN_MS - elapsed;

	return remaining > 0 ? remaining : 0;
};

export const formatCooldownRemaining = (remainingMs: number): string => {
	const minutes = Math.ceil(remainingMs / (60 * 1000));
	if (minutes <= 1) {
		return 'less than a minute';
	}
	return `${minutes} minutes`;
};

export const useSyncInventoryMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: syncInventoryItem,
		onSuccess: (_data, variables) => {
			variables.inventoryIds.forEach((inventoryId) => {
				queryClient.invalidateQueries({
					queryKey: queries.inventory.detail(inventoryId).queryKey,
				});
			});
			queryClient.invalidateQueries({
				queryKey: queries.inventory.list._def,
			});
		},
	});
};
